import { Fragment, useEffect, useMemo, useState } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import { Badge, Button, Card, Col, Dropdown, Form, Row, Spinner, Table } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";
import SignedBillActions from "../Common/SignedBillActions";

const money = (value) =>
 new Intl.NumberFormat("en-IN", {
 style: "currency",
 currency: "INR",
 }).format(Number(value || 0));

const statusVariant = (status) => {
 if (status === "Paid") return "success";
 if (status === "Partially Paid") return "warning";
 if (status === "Rejected") return "danger";
 if (status === "Submitted") return "info";
 return "secondary";
};

const getItemKey = (item) =>
 [item.itemName || "", item.unit || "", item.description || ""]
 .map((value) => String(value).trim().toLowerCase())
 .filter(Boolean)
 .join("|");

const getAvailableStages = (vwo) => {
 if (!vwo) return [];
 const stages = new Set();
 const mode = vwo.paymentMode || "CUSTOM";
 (vwo.paymentTerms || []).forEach((t) => {
 const label = mode === "STANDARD" ? t.stage : t.name;
 if (label?.trim()) stages.add(label.trim());
 });
 (vwo.items || []).forEach((item) => {
 if (!item.paymentTerms?.length) return;
 const iMode = item.paymentMode || "CUSTOM";
 item.paymentTerms.forEach((t) => {
 const label = iMode === "STANDARD" ? t.stage : t.name;
 if (label?.trim()) stages.add(label.trim());
 });
 });
 return Array.from(stages);
};

// Vertical three-dot icon
const svgMenu = (
 <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
 <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
 <rect x="0" y="0" width="24" height="24" />
 <circle fill="#000000" cx="12" cy="5" r="2" />
 <circle fill="#000000" cx="12" cy="12" r="2" />
 <circle fill="#000000" cx="12" cy="19" r="2" />
 </g>
 </svg>
);

const VendorWorkOrderBillingList = () => {
 const navigate = useNavigate();
 const [searchParams] = useSearchParams();
 const vendorWorkOrderFromUrl = searchParams.get("vendorWorkOrder") || "";
 const workOrderFromUrl = searchParams.get("workOrder") || "";
 const [vendorWorkOrders, setVendorWorkOrders] = useState([]);
 const [billings, setBillings] = useState([]);
 const [loading, setLoading] = useState(true);
 const [filters, setFilters] = usePersistentFilters("vendor_wo_billing_filters", {
 search: "",
 status: "",
 });
 const [downloadingPdf, setDownloadingPdf] = useState(null);
 const [previewingPdf, setPreviewingPdf] = useState(null);

 const loadBillingQueue = async () => {
 try {
 setLoading(true);
 const [woRes, billRes] = workOrderFromUrl
 ? await Promise.all([
 api.get(`/vendor-work-orders?workOrder=${workOrderFromUrl}&limit=1000`),
 api.get(`/vendor-work-order-billings?workOrder=${workOrderFromUrl}&limit=1000`),
 ])
 : vendorWorkOrderFromUrl
 ? await Promise.all([
 api.get(`/vendor-work-orders/${vendorWorkOrderFromUrl}`),
 api.get(`/vendor-work-order-billings?vendorWorkOrder=${vendorWorkOrderFromUrl}&limit=1000`),
 ])
 : await Promise.all([
 api.get("/vendor-work-orders?limit=1000"),
 api.get("/vendor-work-order-billings?limit=1000"),
 ]);

  const workOrders = (
    woRes.data.success
      ? (vendorWorkOrderFromUrl && !workOrderFromUrl ? [woRes.data.data] : woRes.data.data || [])
      : []
  ).filter((wo) => wo && wo.project && (wo.project._id || wo.project.projectName));

  const bills = (billRes.data.success ? billRes.data.data || [] : []).filter(
    (b) =>
      b.vendorWorkOrder &&
      b.vendorWorkOrder.project &&
      (b.vendorWorkOrder.project._id || b.vendorWorkOrder.project.projectName)
  );

  setVendorWorkOrders(workOrders);
  setBillings(bills);
 } catch (error) {
 swal(
 "Error",
 error.response?.data?.message || "Failed to load vendor billing list",
 "error"
 );
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 loadBillingQueue();
 }, []);

 const billsByVendorWorkOrder = useMemo(() => {
 const map = new Map();
 billings.forEach((bill) => {
 const vendorWorkOrderId = bill.vendorWorkOrder?._id || bill.vendorWorkOrder;
 if (!vendorWorkOrderId) return;
 const existing = map.get(vendorWorkOrderId) || [];
 map.set(vendorWorkOrderId, [...existing, bill]);
 });
 return map;
 }, [billings]);

 const workOrderRows = useMemo(() => {
 const search = filters.search.trim().toLowerCase();
 const billRows = [];

 vendorWorkOrders.forEach((wo) => {
 const woBills = billsByVendorWorkOrder.get(wo._id) || [];
 const approvedBills = woBills.filter((bill) => bill.status !== "Rejected");
 const billedQuantityByItem = new Map();
 
 approvedBills.forEach((bill) => {
 (bill.items || []).forEach((item) => {
 const itemKey = getItemKey(item);
 billedQuantityByItem.set(
 itemKey,
 Number(billedQuantityByItem.get(itemKey) || 0) + Number(item.quantity || 0)
 );
 });
 });

 // Check if there are remaining stages to bill (bill-level stages)
 const allBilledStages = new Set();
 approvedBills.forEach((bill) => {
 const billStages = bill.stages || [];
 billStages.forEach(stage => allBilledStages.add(stage));
 });
 
 const availableStages = getAvailableStages(wo);
 const hasRemainingStages = availableStages.some(stage => !allBilledStages.has(stage));
 
 // Determine billed status based on stages
 const allStagesBilled = availableStages.length > 0 && availableStages.every(stage => allBilledStages.has(stage));
 const someStagesBilled = allBilledStages.size > 0 && !allStagesBilled;

 const hasRemainingQuantity = (wo.items || []).some(
 (item) => {
 const itemKey = getItemKey(item);
 const billedStages = allBilledStages;
 const availableStages = getAvailableStages(wo);
 
 // If stages exist and some are billed, consider quantity as remaining (stage-wise billing)
 if (availableStages.length > 0 && billedStages.size > 0) {
 return true; // Allow partial stage billing
 }
 
 // Normal quantity check for non-stage billing
 return Number(item.quantity || 0) > Number(billedQuantityByItem.get(itemKey) || 0);
 }
 );
 
 const paid = woBills.reduce(
 (sum, bill) => sum + Number(bill?.paidAmount || 0),
 0
 );
 const remaining = Math.max(Number(wo.totalAmount || 0) - paid, 0);
 const latestBillId = woBills[0]?._id;

 // Add rows for each bill
 if (woBills.length > 0) {
 woBills.forEach((bill) => {
 const paidBill = Number(bill?.paidAmount || 0) > 0 && 
 Number(bill?.paidAmount || 0) < Number(bill?.totalAmount || 0);
 const fullyPaidBill = Number(bill?.paidAmount || 0) >= Number(bill?.totalAmount || 0) && 
 Number(bill?.totalAmount || 0) > 0;
 
 const billingStatus = paidBill
 ? "Partially Paid"
 : fullyPaidBill
 ? "Paid"
 : bill.status || "Submitted";

 // Show "Create Next Bill" only for latest bill if there are remaining stages AND remaining quantity
 const showCreateNextBill = (hasRemainingStages && hasRemainingQuantity && bill._id === latestBillId) || (hasRemainingStages && bill._id === latestBillId);

 billRows.push({
 wo,
 bill,
 paid: Number(bill?.paidAmount || 0),
 remaining: Math.max(Number(bill?.totalAmount || 0) - Number(bill?.paidAmount || 0), 0),
 hasRemainingQuantity,
 hasRemainingStages,
 allStagesBilled,
 someStagesBilled,
 billingStatus,
 isBillRow: true,
 showCreateNextBill,
 });
 });
 } else {
 // Add row for work order without bills
 billRows.push({
 wo,
 bill: null,
 paid: 0,
 remaining: Number(wo.totalAmount || 0),
 hasRemainingQuantity: true,
 hasRemainingStages: true,
 allStagesBilled: false,
 someStagesBilled: false,
 billingStatus: "Pending",
 isBillRow: false,
 showCreateNextBill: true,
 });
 }
 });

 // Filter by search
 const filteredRows = billRows.filter((row) => {
 if (!search) return true;
 const vendorName = row.wo.vendor?.name || row.wo.vendor?.companyName || "";
 return [
 row.wo.woNumber,
 row.wo.title,
 row.wo.description,
 vendorName,
 row.bill?.billNo,
 row.bill?.invoiceNo,
 ]
 .some((value) => String(value || "").toLowerCase().includes(search));
 });

 // Filter by status
 return filteredRows.filter((row) => {
 if (!filters.status) return true;
 if (filters.status === "Pending") return !row.bill;
 if (filters.status === "Bill Pending") return !row.bill;
 return row.billingStatus === filters.status;
 });
 }, [billsByVendorWorkOrder, vendorWorkOrders, filters.search, filters.status]);

 const handleCreateBill = (vendorWorkOrderId) => {
 const row = workOrderRows.find((r) => r.wo._id === vendorWorkOrderId);
 if (row && !row.hasRemainingQuantity && !row.hasRemainingStages) {
 swal("Fully Billed", "All items have been fully billed for this work order.", "info");
 return;
 }
 navigate(`/vendor-work-order-billing/add?vendorWorkOrder=${vendorWorkOrderId}`);
 };

 const handleSearch = () => loadBillingQueue();

 const handlePreviewPDF = async (billId) => {
 setPreviewingPdf(billId);
 const previewWindow = window.open("", "_blank");
 try {
 const res = await api.get(`/vendor-work-order-billings/${billId}/pdf`, {
 params: { mode: "preview" },
 responseType: "blob",
 timeout: 120000,
 suppressErrorToast: true,
 });
 const contentType = res.headers?.["content-type"] || "";
 if (!contentType.includes("application/pdf")) {
 if (previewWindow) previewWindow.close();
 swal("Error", "Failed to generate PDF preview", "error");
 return;
 }
 const fileURL = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
 if (previewWindow) previewWindow.location.href = fileURL;
 else window.open(fileURL, "_blank");
 } catch (err) {
 if (previewWindow) previewWindow.close();
 swal("Error", err.response?.data?.message || "Failed to preview PDF", "error");
 } finally {
 setPreviewingPdf(null);
 }
 };

 const handleDownloadPDF = async (billId, billNo) => {
 setDownloadingPdf(billId);
 try {
 const res = await api.get(`/vendor-work-order-billings/${billId}/pdf`, {
 responseType: "blob",
 timeout: 120000,
 });
 const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
 const link = document.createElement("a");
 link.href = url;
 link.download = `Vendor-Bill-${billNo || billId}.pdf`;
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
 window.URL.revokeObjectURL(url);
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to download PDF", "error");
 } finally {
 setDownloadingPdf(null);
 }
 };

 return (
 <Fragment>
 <PageTitle activeMenu="Vendor Billing" motherMenu="Commercial" />
 <Card className="shadow-sm border-0">
 <Card.Header className="bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
 <Card.Title className="mb-0">Vendor Billing</Card.Title>
 <div className="d-flex gap-2">
 <Button variant="light" onClick={() => navigate("/purchase-orders-by-project")}>
 Back
 </Button>
 <Button variant="outline-primary" onClick={() => navigate("/vendor-work-orders")}>
 Vendor Work Orders
 </Button>
 <Button variant="primary" onClick={() => navigate("/vendor-work-order-billing/add")}>
 Create Vendor Bill
 </Button>
 </div>
 </Card.Header>
 {vendorWorkOrderFromUrl && (
 <Card.Body className="pb-0">
 <div className="alert alert-info mb-0">
 Filtered by vendor work order.
 </div>
 </Card.Body>
 )}

 <Card.Body>
 <Row className="g-2 mb-3 align-items-end">
 <Col md={5}>
 <Form.Control
 placeholder="Search WO number, title, vendor..."
 value={filters.search}
 onChange={(e) =>
 setFilters((prev) => ({ ...prev, search: e.target.value }))
 }
 onKeyDown={(e) => e.key === "Enter" && handleSearch()}
 />
 </Col>
 <Col md={3}>
 <Form.Select
 value={filters.status}
 onChange={(e) =>
 setFilters((prev) => ({ ...prev, status: e.target.value }))
 }
 >
 <option value="">All Status</option>
 <option value="Pending">Pending</option>
 <option value="Submitted">Submitted</option>
 <option value="Partially Paid">Partially Paid</option>
 <option value="Paid">Paid</option>
 <option value="Rejected">Rejected</option>
 </Form.Select>
 </Col>
 <Col md="auto">
 <Button variant="primary" onClick={handleSearch}>
 Search
 </Button>
 </Col>
 </Row>

 {loading ? (
 <div className="text-center py-5">
 <Spinner />
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover className="align-middle">
 <thead>
 <tr>
 <th>Vendor WO</th>
 <th>Title</th>
 <th>Vendor</th>
 <th>Items</th>
 <th>WO Value</th>
 <th>Bill No</th>
 <th>Bill Amount</th>
 <th>Paid</th>
 <th>Remaining</th>
 <th>Status</th>
 <th>Signed Bill</th>
 <th className="text-end">Actions</th>
 </tr>
 </thead>
 <tbody>
 {workOrderRows.map(({ wo, bill, paid, remaining, hasRemainingQuantity, hasRemainingStages, allStagesBilled, someStagesBilled, billingStatus, isBillRow, showCreateNextBill }) => {
 return (
 <tr key={bill ? bill._id : wo._id}>
 <td>
 <strong>{wo.woNumber || "-"}</strong>
 </td>
 <td>{wo.title || "-"}</td>
 <td>{wo.vendor?.name || wo.vendor?.companyName || "-"}</td>
 <td>{wo.items?.length || 0}</td>
 <td>{money(Number(wo.totalAmount || 0))}</td>
 <td>
 {bill ? (
 <>
 <strong>{bill.billNo}</strong>
 <div className="small text-muted">{bill.invoiceNo || ""}</div>
 </>
 ) : (
 <span className="text-muted">Not created</span>
 )}
 </td>
 <td>
 {bill ? money(Number(bill.totalAmount || 0)) : "-"}
 </td>
 <td>{money(paid)}</td>
 <td>{money(remaining)}</td>
 <td>
 <Badge bg={statusVariant(billingStatus)}>
 {bill ? (
 allStagesBilled ? "Fully Billed" :
 someStagesBilled ? "Partially Billed" :
 billingStatus
 ) : "Bill Pending"}
 </Badge>
 </td>
 <td>
 {bill ? (
 <div className="d-flex flex-column gap-1">
 <div className="small text-muted">
 {bill.signedCopy?.uploadedAt
 ? new Date(bill.signedCopy.uploadedAt).toLocaleDateString("en-IN")
 : "Not uploaded"}
 </div>
 <SignedBillActions
 id={bill._id}
 type="vendor-work-order-billings"
 signedCopy={bill.signedCopy}
 onUploadSuccess={loadBillingQueue}
 />
 </div>
 ) : (
 <span className="text-muted">-</span>
 )}
 </td>
 <td className="text-end">
 <div className="d-flex justify-content-end align-items-center gap-2 flex-nowrap">
 {bill ? (
 <div className="d-flex justify-content-end align-items-center gap-2">
 {showCreateNextBill && (
 <Button
 size="sm"
 variant="outline-primary"
 onClick={() => handleCreateBill(wo._id)}
 >
 Create Next Bill
 </Button>
 )}
 {/* Three-dot dropdown */}
 <Dropdown drop="start">
 <Dropdown.Toggle
 as="div"
 bsPrefix="dropdown-toggle-no-caret"
 style={{ cursor: "pointer", lineHeight: 1, display: "inline-block" }}
 >
 {svgMenu}
 </Dropdown.Toggle>
 <Dropdown.Menu
 align="end"
 popperConfig={{ strategy: "fixed" }}
 style={{ zIndex: 2000 }}
 >
 <Dropdown.Item
 onClick={() => navigate(`/vendor-work-order-billing/view/${bill._id}`)}
 >
 <i className="fas fa-eye me-2 text-primary" />
 View
 </Dropdown.Item>
 <Dropdown.Divider />
 <Dropdown.Item
 disabled={previewingPdf === bill._id}
 onClick={() => handlePreviewPDF(bill._id)}
 >
 <i className="fas fa-eye me-2 text-info" />
 {previewingPdf === bill._id ? "Loading..." : "Preview PDF"}
 </Dropdown.Item>
 <Dropdown.Item
 disabled={downloadingPdf === bill._id}
 onClick={() => handleDownloadPDF(bill._id, bill.billNo)}
 >
 <i className="fas fa-file-pdf me-2 text-danger" />
 {downloadingPdf === bill._id ? "Downloading..." : "Download PDF"}
 </Dropdown.Item>
 </Dropdown.Menu>
 </Dropdown>
 </div>
 ) : hasRemainingStages ? (
 <Button
 size="sm"
 variant="outline-primary"
 onClick={() => handleCreateBill(wo._id)}
 >
 Create Bill
 </Button>
 ) : (
 <Badge bg="success" className="px-2 py-1">
 <i className="fas fa-check me-1"></i> Fully Billed
 </Badge>
 )}
 </div>
 </td>
 </tr>
 );
 })}
 {workOrderRows.length === 0 && (
 <tr>
 <td colSpan="12" className="text-center text-muted py-4">
 No vendor work orders found.
 </td>
 </tr>
 )}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 </Card>
 </Fragment>
 );
};

export default VendorWorkOrderBillingList;
